import { ReactNode, CSSProperties } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";
import Tooltip from "@atlaskit/tooltip";

import { InfoIcon } from "@/components/wpl/pricing/src/price-finder/price-card/info-icon";
import {
    Button,
    ButtonProps
} from "@/libraries/design/components/button/src/button";
import { Expand } from "@/libraries/design/components/expand/src/expand";
import { Link } from "@/libraries/design/components/link/src/link";
import { wacToken } from "@/libraries/design/foundations";
import { TypographyStyles } from "@/libraries/wpl/foundations/src/typography-styles";

export interface PricingCardProps {
    description?: string | ReactNode;
    price?: string | ReactNode;
    currencyIsoCode?: string;
    priceAltElement?: ReactNode;
    title: string | ReactNode;
    bannerText?: string | ReactNode;
    unit?: string | ReactNode;
    range?: string | ReactNode;
    buttonText: string | ReactNode;
    buttonDisabled?: boolean;
    benefitsTitle?: string | ReactNode;
    benefits?: { label: string; details?: ReactNode; lozengeText?: string }[];
    buttonVariant?: "primary" | "secondary";
    buttonHref?: string;
    tooltipContent?: ReactNode;
    buttonProps?: Partial<ButtonProps>;
    calloutText?: string | ReactNode;
    link?: string | ReactNode;
    linkText?: string | ReactNode;
    topSectionStyle?: CSSProperties;
    bottomSectionStyle?: CSSProperties;
    // the PricingCard component is typically used in the context of the PriceCalculator component
    // where the PriceCalculator component handles the layout and border styling of the pricing cards
    // however, if the PricingCard component is used in a standalone context, this prop can be used
    isSingleCard?: boolean;
    hasUnitSlot?: boolean;
    hasLinkSlot?: boolean;
    hasCalloutSlot?: boolean;
    hasRangeSlot?: boolean;
    hasBannerSlot?: boolean;
    priceTestid?: string;
}

// TODO: don't show slots if in mobile viewport?
// eslint-disable-next-line complexity
export const PricingCard = (props: PricingCardProps) => {
    const {
        bannerText,
        title,
        unit,
        range,
        buttonText,
        buttonDisabled,
        benefitsTitle,
        benefits,
        buttonVariant = "primary",
        buttonHref,
        description,
        currencyIsoCode,
        price,
        priceAltElement,
        tooltipContent,
        buttonProps,
        calloutText,
        link,
        linkText,
        topSectionStyle,
        bottomSectionStyle,
        isSingleCard,
        hasUnitSlot,
        hasLinkSlot,
        hasCalloutSlot,
        hasRangeSlot,
        hasBannerSlot,
        priceTestid
    } = props;

    const topSectionRecommendedStyles = css({
        borderBlockStart: `1px solid ${wacToken("color.primary.black")}`,
        borderInlineStart: `1px solid ${wacToken("color.primary.black")}`,
        borderInlineEnd: `1px solid ${wacToken("color.primary.black")}`,
        borderBlockEnd: !benefits
            ? `1px solid ${wacToken("color.primary.black")}`
            : undefined
    });

    const bottomSectionRecommendedStyles = css({
        borderBlockEnd: `1px solid ${wacToken("color.primary.black")}`,
        borderInlineStart: `1px solid ${wacToken("color.primary.black")}`,
        borderInlineEnd: `1px solid ${wacToken("color.primary.black")}`
    });

    const topSectionNeutralStyles = css({
        borderBlockStart: `1px solid ${wacToken("color.neutral.light400")}`,
        borderInlineStart: `1px solid ${wacToken("color.neutral.light400")}`,
        borderInlineEnd: `1px solid ${wacToken("color.neutral.light400")}`,
        borderBlockEnd: !benefits
            ? `1px solid ${wacToken("color.neutral.light400")}`
            : undefined
    });

    const bottomSectionNeutralStyles = css({
        borderBlockEnd: `1px solid ${wacToken("color.neutral.light400")}`,
        borderInlineStart: `1px solid ${wacToken("color.neutral.light400")}`,
        borderInlineEnd: `1px solid ${wacToken("color.neutral.light400")}`
    });

    const containerStyles = css({
        borderStartStartRadius: isSingleCard
            ? `${wacToken("corner.radius.card")}`
            : undefined,
        borderStartEndRadius: isSingleCard
            ? `${wacToken("corner.radius.card")}`
            : undefined,
        borderEndStartRadius: !benefits
            ? `${wacToken("corner.radius.card")}`
            : undefined,
        borderEndEndRadius: !benefits
            ? `${wacToken("corner.radius.card")}`
            : undefined,
        minWidth: "220px"
    });

    const bottomContainerStyles = css({
        borderRadius: !benefits
            ? `${wacToken("corner.radius.card")}`
            : undefined,
        borderEndStartRadius: isSingleCard
            ? `${wacToken("corner.radius.card")}`
            : undefined,
        borderEndEndRadius: isSingleCard
            ? `${wacToken("corner.radius.card")}`
            : undefined
    });

    return (
        <>
            <div
                css={[
                    containerStyles,
                    isSingleCard && bannerText
                        ? topSectionRecommendedStyles
                        : null,
                    isSingleCard && !bannerText ? topSectionNeutralStyles : null
                ]}
                style={{ ...topSectionStyle }}
            >
                {bannerText ? (
                    <div
                        css={css({
                            backgroundColor: wacToken("color.primary.black"),
                            borderStartStartRadius: "inherit",
                            borderStartEndRadius: "inherit",
                            // to ensure the banner color and border sufficiently overlap each other
                            marginInlineStart: `-1px`,
                            marginInlineEnd: `-1px`
                        })}
                    >
                        <TypographyStyles>
                            <p
                                className="body-sm"
                                style={{
                                    // override the color from the typography styles
                                    color: wacToken("color.neutral.light0"),
                                    // doesn't map to a type matrix in the design, so override
                                    fontSize: "0.75rem"
                                }}
                                css={css({
                                    margin: 0,
                                    textAlign: "center",
                                    textTransform: "uppercase"
                                })}
                            >
                                {bannerText}
                            </p>
                        </TypographyStyles>
                    </div>
                ) : hasBannerSlot ? (
                    <div
                        css={css({
                            paddingBlockStart: token("space.250")
                        })}
                    />
                ) : null}
                <div
                    css={css({
                        paddingInlineStart: token("space.300"),
                        paddingInlineEnd: token("space.300"),
                        paddingBlockStart: token("space.300"),
                        display: "flex",
                        height: "100%",
                        flexDirection: "column",
                        alignItems: "center",
                        [media.above.md]: {
                            paddingBlockEnd: token("space.600")
                        },
                        [media.below.md]: {
                            paddingBlockEnd: token("space.300")
                        }
                    })}
                >
                    <div
                        css={css({
                            "display": "flex",
                            "flexDirection": "column",
                            "alignItems": "center",
                            "height": "100%",
                            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                            ".pricing-card-button": {
                                minHeight: "44px"
                            }
                        })}
                    >
                        <div
                            css={css({
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                height: "100%",
                                [media.below.md]: {
                                    marginBlockEnd: token("space.300")
                                }
                            })}
                        >
                            <div
                                css={css({
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    height: "100%"
                                })}
                            >
                                <div>
                                    <TypographyStyles>
                                        {/* override the margin from the h4 */}
                                        <h4
                                            css={css({
                                                textAlign: "center",
                                                marginBlockEnd: 0,
                                                marginBlockStart: 0
                                            })}
                                        >
                                            {title}
                                        </h4>
                                    </TypographyStyles>
                                    <TypographyStyles>
                                        {/* negate the margin from the p */}
                                        <p
                                            className="body-sm"
                                            css={css({
                                                margin: 0,
                                                textAlign: "center"
                                            })}
                                        >
                                            {description}
                                        </p>
                                    </TypographyStyles>
                                </div>
                                {price && (
                                    <div>
                                        {/* negate the margin from the h2 */}
                                        {price && (
                                            <h2
                                                className="pricing-card-price"
                                                data-testid={priceTestid}
                                                css={[
                                                    css({
                                                        margin: 0,
                                                        paddingBlockStart:
                                                            token("space.300"),
                                                        textAlign: "center"
                                                    }),
                                                    currencyIsoCode === "JPY" &&
                                                        css({
                                                            "&.pricing-card-price":
                                                                {
                                                                    fontSize:
                                                                        "34px"
                                                                }
                                                        })
                                                ]}
                                            >
                                                {price}
                                            </h2>
                                        )}
                                    </div>
                                )}
                                {!price && (
                                    <TypographyStyles>
                                        {priceAltElement}
                                    </TypographyStyles>
                                )}
                            </div>
                            {unit && price && (
                                <TypographyStyles>
                                    <p
                                        className="body-sm"
                                        css={css({
                                            textAlign: "center",
                                            margin: 0,
                                            paddingBlockEnd: range
                                                ? undefined
                                                : token("space.300")
                                        })}
                                    >
                                        {unit}
                                    </p>
                                </TypographyStyles>
                            )}

                            {!unit && hasUnitSlot && (
                                <div
                                    className="unit-slot"
                                    css={css({
                                        minHeight: "44px"
                                    })}
                                />
                            )}
                            {range ? (
                                <TypographyStyles>
                                    <div
                                        css={css({
                                            display: "flex",
                                            flexDirection: "row",
                                            margin: 0,
                                            paddingBlockEnd: token("space.300")
                                        })}
                                    >
                                        <p
                                            className="body-sm"
                                            css={css({ margin: 0 })}
                                        >
                                            {range}
                                        </p>
                                        <div
                                            css={css({
                                                margin: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                marginInlineStart:
                                                    token("space.050")
                                            })}
                                        >
                                            {tooltipContent && (
                                                <Tooltip
                                                    content={tooltipContent}
                                                >
                                                    <span>
                                                        <InfoIcon size="small" />
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                </TypographyStyles>
                            ) : hasRangeSlot ? (
                                <div
                                    className="range-slot"
                                    css={css({
                                        height: "24.5px",
                                        paddingBlockEnd: token("space.300")
                                    })}
                                />
                            ) : null}
                            {(calloutText || hasCalloutSlot) && (
                                <div
                                    css={css({
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    })}
                                >
                                    {calloutText ? (
                                        <TypographyStyles>
                                            <p
                                                className="body-sm"
                                                css={css({
                                                    margin: 0,
                                                    textAlign: "center",
                                                    paddingBlockEnd:
                                                        token("space.300")
                                                })}
                                            >
                                                {calloutText}
                                            </p>
                                        </TypographyStyles>
                                    ) : hasCalloutSlot ? (
                                        <div
                                            css={css({
                                                paddingBlockEnd: `calc(${token(
                                                    "space.800"
                                                )} + ${token("space.100")})`
                                            })}
                                        />
                                    ) : null}
                                </div>
                            )}
                        </div>

                        <Button
                            variant={buttonVariant}
                            size="small"
                            isDisabled={buttonDisabled}
                            href={buttonHref}
                            target="_self"
                            data-testid="cta-button"
                            {...buttonProps}
                            className="pricing-card-button"
                        >
                            {buttonText}
                        </Button>
                    </div>
                    {(typeof link === "string" && linkText) ||
                    typeof link === "object" ? (
                        <div
                            css={css({
                                paddingBlockStart: "20px"
                            })}
                        >
                            {typeof link === "string" && (
                                <Link
                                    href={link}
                                    type="primary"
                                    text={String(linkText)}
                                    aria-label={String(linkText)}
                                />
                            )}

                            {typeof link === "object" && <>{link}</>}
                        </div>
                    ) : hasLinkSlot ? (
                        <div
                            css={css({
                                // only show the link slot on desktop if the group of pricing cards
                                // has a link
                                [media.above.md]: {
                                    paddingBlockStart: token("space.300"),
                                    marginBlockEnd: "20px"
                                },
                                // on mobile, hide the link slot because we don't care about
                                // link and button alignment
                                [media.below.md]: {
                                    display: "none"
                                }
                            })}
                        >
                            {linkText}
                        </div>
                    ) : null}
                </div>
            </div>

            {benefits && (
                <div
                    css={[
                        bottomContainerStyles,
                        isSingleCard && bannerText
                            ? bottomSectionRecommendedStyles
                            : null,
                        isSingleCard && !bannerText
                            ? bottomSectionNeutralStyles
                            : null
                    ]}
                    style={{ ...bottomSectionStyle }}
                >
                    <div
                        css={css({
                            marginInlineStart: token("space.300"),
                            marginInlineEnd: token("space.300"),
                            borderBlockStart: `1px solid ${wacToken(
                                "color.neutral.light300"
                            )}`
                        })}
                    />
                    <div
                        css={css({
                            padding: token("space.300"),
                            display: "flex",
                            gap: token("space.200"),
                            flexDirection: "column"
                        })}
                    >
                        <TypographyStyles>
                            <p
                                // doesn't map to a type matrix in the design
                                className="body-md"
                                css={css({ margin: 0 })}
                            >
                                <span css={css({ fontWeight: 600 })}>
                                    {benefitsTitle}
                                </span>
                            </p>
                        </TypographyStyles>
                        {benefits?.map(({ label, details, lozengeText }) =>
                            details ? (
                                <Expand
                                    key={label}
                                    label={label}
                                    eventDataName={label}
                                    lozengeText={lozengeText}
                                >
                                    {details}
                                </Expand>
                            ) : (
                                <TypographyStyles key={label}>
                                    <p
                                        className="body-sm"
                                        css={css({
                                            margin: 0,
                                            paddingInlineStart: `calc(${token(
                                                "space.300"
                                            )} + ${token("space.050")})`
                                        })}
                                    >
                                        {label}
                                    </p>
                                </TypographyStyles>
                            )
                        )}
                    </div>
                </div>
            )}
        </>
    );
};
